'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Swal from 'sweetalert2';
import { authClient } from '@bip/api-client';
import { AccountShell } from '../hotels/_components/AccountShell';
import { LoginGate } from '../hotels/_components/LoginGate';
import { PROXY_PREFIX } from '../hotels/config';
import { PasswordFormSkeleton } from '../_components/Skeleton';

const HOME_HREF = `${PROXY_PREFIX}/hotels`;

// Same container/input styling as `my-profile` (square borders, `#ced4da `, stacked rows), but
// WITHOUT that page's background-color/breadcrumb overrides (stays this page's own AccountShell
// defaults: `bg-yellow-light` + the purple breadcrumb) and full-width instead of `max-w-2xl`.
// `!` (important) on font-size/label-size — globals.css has UNLAYERED `h2,h3,h4,h5,h6 { ... }`
// (font-weight/family only, no size) and `label { font-size: var(--text-md) }` rules that beat any
// `@layer utilities` class regardless of specificity (this project's CLAUDE.md Tailwind-cascade
// gotchas) — `<h2>` itself already gets font-weight:500/font-family:owners for free from that rule.
const labelCls = 'text-[0.9375rem]! font-semibold text-secondary';
const errorCls = 'text-xs text-red-600 mt-1';
// Change Password's own reference markup (`my-profile.html` lines 257-267) uses a plain
// `form-group` (label stacked above its input) for this form — unlike the side-by-side
// `form-group row` used for the rest of the profile fields — so this page keeps the input on
// its own line below the label rather than reusing my-profile's `grid-cols-[180px_1fr]` row.
const rowCls = 'flex flex-col gap-1.5';
const inputBase = 'w-full border rounded-none! px-3 py-2.5 text-sm bg-white outline-none transition-colors';
type FieldVisualState = 'error' | 'valid' | 'none';
// Bootstrap 4's own focus treatment: a solid border colour plus a soft 0.2rem colour-matched
// glow (`box-shadow: 0 0 0 .2rem rgba(…,.25)`) instead of a purple ring — the glow tints red/green
// on the two validation states instead of always being the default blue.
const borderClsFor = (s: FieldVisualState) =>
  s === 'error'
    ? 'border-[#dc3545] focus:border-[#dc3545] focus:shadow-[0_0_0_0.2rem_rgba(220,53,69,0.25)]'
    : s === 'valid'
      ? 'border-[#28a745] focus:border-[#28a745] focus:shadow-[0_0_0_0.2rem_rgba(40,167,69,0.25)]'
      : 'border-[#ced4da] focus:border-[#80bdff] focus:shadow-[0_0_0_0.2rem_rgba(0,123,255,0.25)]';

// Exact wording from the reference `formValidation.js` `checkInvalidRequired` — every required
// field falls through its `switch(uiDataType)` to this same message.
const REQUIRED_MSG = 'Please fill out this field.';
// Exact wording from `change-password.js`'s WINNING `checkConfirmPassword` — the page loads
// `formValidation.js` first (defines a generic version checked against `#passwordPrimary`, message
// "Password Does not match") THEN `changePassword.js` (redefines the SAME function name against
// `.newfieldPassw`, message "Does not match New Password") — the later script's function
// declaration overwrites the earlier one, so this is the one that actually runs.
const CONFIRM_MISMATCH_MSG = 'Does not match New Password';
// Exact wording from `checkOldVsNewPassword` in the reference.
const SAME_AS_OLD_MSG = "New Password field shouldn't match with the Existing Password";

// Same strength rule the auth-service enforces (Change Password.html:67): ≥8 chars with a
// lowercase, an uppercase, a digit, and one of the explicitly-allowed special characters.
const PASSWORD_STRENGTH = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$£%^&*()[\]]).{8,}$/;
const STRENGTH_HINT =
  'Password must be at least 8 characters long, containing at least one uppercase character, one lowercase character, one digit and one of the following special characters: ! @ # $ £ % ^ & * ( ) [ ].';

const schema = z
  .object({
    oldPassword: z.string().min(1, REQUIRED_MSG),
    newPassword: z.string().min(1, REQUIRED_MSG).regex(PASSWORD_STRENGTH, STRENGTH_HINT),
    confirmPassword: z.string().min(1, REQUIRED_MSG),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: CONFIRM_MISMATCH_MSG,
  })
  .refine((d) => d.newPassword !== d.oldPassword, {
    path: ['newPassword'],
    message: SAME_AS_OLD_MSG,
  });
type FormValues = z.infer<typeof schema>;

/** Password input with a show/hide eye toggle. Validation state shows as a border colour +
 *  focus glow only (no tick/exclamation overlay icon). */
function PasswordField({
  label,
  placeholder,
  hint,
  error,
  state,
  registration,
}: {
  label: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  state: FieldVisualState;
  registration: UseFormRegisterReturn;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className={rowCls}>
      <label className={labelCls}>
        {label}
        <em className="text-red-600 not-italic ml-0.5">*</em>
        {hint && (
          <span className="ml-1 cursor-help group relative align-middle">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-black text-white text-[10px] leading-none font-serif italic">
              i
            </span>
            <span className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 w-80 bg-black/90 text-white text-sm leading-relaxed rounded px-3 py-2.5 font-normal text-left">
              {hint}
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-black/90" />
            </span>
          </span>
        )}
      </label>
      <div>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            placeholder={placeholder}
            aria-invalid={state === 'error'}
            {...registration}
            className={`${inputBase} ${borderClsFor(state)} pr-10`}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-text hover:text-primary transition-colors"
            tabIndex={-1}
            aria-label={show ? 'Hide password' : 'Show password'}
          >
            <i className={`fa-regular ${show ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        </div>
        {error && <p className={errorCls}>{error}</p>}
      </div>
    </div>
  );
}

export default function ChangePasswordPage() {
  const router = useRouter();
  const [auth, setAuth] = useState<'loading' | 'in' | 'out'>('loading');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, submitCount },
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  // Green tick / red icon — same rule as `my-profile`: red on error, green once a submit attempt
  // has run and the field passed (there's no separate "saved" moment here like the profile-update
  // Swal, since a successful password change immediately navigates away).
  const stateOf = (name: keyof FormValues): FieldVisualState =>
    errors[name] ? 'error' : submitCount > 0 ? 'valid' : 'none';

  useEffect(() => {
    let cancelled = false;
    authClient
      .getMe()
      .then((me) => {
        if (cancelled) return;
        if (me.message !== 'success') {
          setAuth('out'); // CMS parity: pop the login modal (LoginGate) instead of bouncing home.
        } else {
          setAuth('in');
        }
      })
      .catch(() => {
        if (!cancelled) setAuth('out');
      });
    return () => {
      cancelled = true;
    };
  }, [router]);

  const onSubmit = async (values: FormValues) => {
    try {
      const res = await authClient.changePassword(values.oldPassword, values.newPassword);
      if (res.errorCode === 3) {
        setError('oldPassword', { message: 'Old password does not match' });
        return;
      }
      if (res.message === 'success') {
        // Exact port of `changePassword.js`'s `Swal.fire({...})` call — same sweetalert2 library
        // this codebase already uses (LoginModal.tsx, my-profile), same params. On confirm, the
        // reference force-logs-out then redirects home — same as the navbar/sidebar Sign Out action.
        const result = await Swal.fire({
          text: 'Password changed. Please Login again',
          icon: 'success',
          confirmButtonColor: '#3c0a52',
          confirmButtonText: 'Login',
        });
        if (result.isConfirmed) {
          await authClient.logout();
          window.location.href = HOME_HREF;
        }
        return;
      }
      setError('newPassword', { message: res.displayMessage || 'Could not change your password.' });
    } catch (err: any) {
      // `authClient.changePassword` throws on any non-2xx response (backend 500s, network
      // failure, etc.) — without this catch, RHF's `handleSubmit` re-throws it as an unhandled
      // rejection AFTER resetting `isSubmitting`, so the button silently re-enables with NO error
      // shown at all (confirmed live — the backend currently 500s on the actual password write
      // whenever the Mongo replica set has no primary). Surface it on the field like every other
      // failure branch above instead.
      setError('newPassword', { message: err?.message || 'Could not change your password. Please try again.' });
    }
  };

  if (auth === 'out') {
    return <LoginGate onHome={() => router.replace(HOME_HREF)} />;
  }
  if (auth !== 'in') {
    return <PasswordFormSkeleton />;
  }

  return (
    <AccountShell crumb="Change Password">
      <div className="bg-white rounded-2xl p-6 lg:p-8 w-full">
        <h2 className="text-[1.625rem]! text-secondary mb-4">Change Password</h2>
        <hr className="border-t border-[#dee2e6] mb-4" />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <PasswordField
            label="Old Password"
            placeholder="Enter Old Password"
            error={errors.oldPassword?.message}
            state={stateOf('oldPassword')}
            registration={register('oldPassword')}
          />
          <PasswordField
            label="New Password"
            placeholder="Enter New Password"
            hint={STRENGTH_HINT}
            error={errors.newPassword?.message}
            state={stateOf('newPassword')}
            registration={register('newPassword')}
          />
          <PasswordField
            label="Confirm New Password"
            placeholder="Confirm New Password"
            error={errors.confirmPassword?.message}
            state={stateOf('confirmPassword')}
            registration={register('confirmPassword')}
          />

          <div className="text-right pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-brand px-10 py-2.5 text-sm disabled:opacity-60"
            >
              {isSubmitting ? 'Changing…' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </AccountShell>
  );
}
